import { useMutation } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import { handlePostRequest } from '../utils/api-handler.util'
import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { showToaster } from '../utils/show-toaster.util'

export function useFileUpload({
  label = 'Upload Image',
  required = true,
  dropZoneOptions = {
    multiple: false,
    disabled: false,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
    },
  },
} = {}) {
  const [isUploading, setIsUploading] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const { currentUser } = useAuth()
  const [errorMessages, setErrorMessages] = useState([])

  const onDrop = (acceptedFiles, fileRejections) => {
    setErrorMessages([])
    if (fileRejections.length > 0) {
      const errors = fileRejections.map((rejection) => {
        return rejection.errors.map((e) => e.message).join(', ')
      })
      setErrorMessages(errors.flat())
    }

    if (!dropZoneOptions.multiple) {
      setSelectedFiles(acceptedFiles)
      return
    }

    const newTotal = selectedFiles.length + acceptedFiles.length

    if (newTotal > (dropZoneOptions.maxFiles || 1)) {
      showToaster('error', `You can only upload up to ${dropZoneOptions.maxFiles} files.`)
      return
    }

    setSelectedFiles((prevFiles) => [...prevFiles, ...acceptedFiles])
  }

  const { getRootProps, getInputProps } = useDropzone({
    ...dropZoneOptions,
    onDrop,
  })

  const removeFile = (fileToRemove) => {
    setSelectedFiles(selectedFiles.filter((file) => file !== fileToRemove))
  }

  const renderFilePreview = (file) => {
    // if it is image, return an image preview
    if (file.type.startsWith('image/')) {
      return (
        <div className="relative group">
          <img
            src={URL.createObjectURL(file)}
            alt={file.name}
            className="w-16 h-16 object-cover rounded"
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              removeFile(file)
            }}
            className="absolute -top-2 -right-2 bg-error-900 text-white rounded-full p-1 opacity-80 hover:opacity-100 transition-opacity"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      )
    }

    // for other file types, return a generic file icon or name
    return (
      <div className="flex items-center justify-between space-x-2 relative group min-w-0">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-foreground font-medium truncate">{file.name}</div>
          <div className="text-xs text-medium-opacity">({(file.size / 1024).toFixed(2)} KB)</div>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            removeFile(file)
          }}
          className="text-error-900 hover:text-red-700 transition-colors flex-shrink-0"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    )
  }

  const maxFilesReached = () => {
    if (dropZoneOptions.maxFiles && dropZoneOptions.multiple) {
      return selectedFiles.length >= dropZoneOptions.maxFiles
    }
    return false
  }

  const renderUploadInput = () => {
    return (
      <div className="space-y-2">
        <label
          htmlFor="imageUpload"
          className="flex items-center text-sm font-medium text-foreground-muted font-inter"
        >
          {label}
          {required && <span className="text-error-900 ml-1">*</span>}
        </label>
        <div
          {...getRootProps({
            className:
              'block w-full px-4 py-6 bg-secondary-50 border-2 border-dashed border-gray-400 rounded text-base font-medium text-foreground font-inter cursor-pointer hover:border-secondary-600 focus:outline-none focus:border-secondary-600 focus:ring-1 focus:ring-secondary-600 transition-colors',
          })}
        >
          <input
            {...getInputProps()}
            required={required && selectedFiles.length === 0}
            disabled={dropZoneOptions.disabled || maxFilesReached()}
          />
          {!selectedFiles.length && (
            <p className="text-center text-medium-opacity">
              {dropZoneOptions.multiple
                ? 'Drag & drop files here, or click to select files'
                : 'Drag & drop a file here, or click to select a file'}
            </p>
          )}
          {selectedFiles.length > 0 && (
            <div className="flex gap-4 justify-center flex-wrap">
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="relative p-3 bg-background border border-secondary-600 rounded-lg shadow-sm"
                >
                  {renderFilePreview(file)}
                </div>
              ))}
            </div>
          )}

          {dropZoneOptions.multiple && (
            <div className="mt-2 text-sm text-foreground-muted text-center">
              {maxFilesReached()
                ? `You have reached the maximum of ${dropZoneOptions.maxFiles} files.`
                : `${selectedFiles.length} files selected. `}
            </div>
          )}
        </div>
        {errorMessages.length > 0 && (
          <div className="text-error-900 text-sm space-y-1">
            {errorMessages.map((msg, idx) => (
              <p key={idx}>{msg}</p>
            ))}
          </div>
        )}
      </div>
    )
  }

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData()
      formData.append('file', file)
      return handlePostRequest(`/${currentUser.userType}s/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
  })

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) return
    const uploadedFileUrls = []
    setIsUploading(true)

    await Promise.all(
      selectedFiles.map(async (file) => {
        const uploadRes = await uploadMutation.mutateAsync(file)
        const fileUrl = uploadRes?.data?.fullPath
        if (fileUrl) {
          uploadedFileUrls.push(fileUrl)
        }
      })
    )
    setIsUploading(false)

    return dropZoneOptions.multiple ? uploadedFileUrls : uploadedFileUrls[0]
  }

  return {
    renderUploadInput,
    handleFileUpload,
    selectedFiles,
    isUploading,
    clearFiles: () => setSelectedFiles([]),
  }
}
